<script setup>
import { ref, computed, onMounted } from 'vue';
import { debounce } from 'lodash-es';
import { trans } from '../../../../_00_elements/translation';
import useSubscriptionStore from '../../store/subscriptions';
import Panel from '../../../../_02_molecules/components/Panel.vue';

const emit = defineEmits([ 'next-step' ]);

const subscriptionStore = useSubscriptionStore();

const offices = ref([]);
const raw = subscriptionStore.selectedValuesList.offices;
const selectedOffices = ref(Array.isArray(raw) ? [ ...raw ] : []);
const isCheckboxChecked = ref(raw === 'unknown');
const searchTerm = ref('');

const statesToText = {
  published: 'En ligne',
  unpublished: 'Hors ligne',
  offmarket: 'Offmarket',
};

const onNewOfficeCheckboxChange = () => {
  if (isCheckboxChecked.value) {
    selectedOffices.value = [];
  }
};

const onOfficeCheckboxChange = () => {
  if (selectedOffices.value.length > 0) {
    isCheckboxChecked.value = false;
  }
};

const getOfficeCheckboxLabel = (office) => `${office.name} - ${office?.address?.number}
    ${office?.address?.street} ${office?.address?.zip_code}\u00A0(${statesToText[office.state]})`;

const isNextDisabled = computed(() => selectedOffices.value?.length === 0 && !isCheckboxChecked.value);

const onClickNext = () => {
  if (isCheckboxChecked.value) {
    subscriptionStore.setSelectedValuesList('offices', 'unknown');
    subscriptionStore.resetOfficeData();
    subscriptionStore.setInvoiceTitle(trans('gestion.subscription.step.2.rent_office'));
  } else if (selectedOffices.value.length > 0) {
    subscriptionStore.setSelectedValuesList('offices', selectedOffices.value);
    subscriptionStore.resetOfficeData();

    const nameOffices = [];
    selectedOffices.value.forEach((officeId) => {
      const selectedOfficeData = offices.value.find((office) => office.id === officeId);
      nameOffices.push(selectedOfficeData.name);
      subscriptionStore.addOfficeData(selectedOfficeData);
    });

    subscriptionStore.setInvoiceTitle(
      `${trans('gestion.subscription.step.2.rent_offices')}${nameOffices.join(', ')}`,
    );
  }
  emit('next-step');
};

const getSignedDeals = async () => {
  await subscriptionStore.fetchOffices({ search: searchTerm.value });
  offices.value = subscriptionStore.officesAvailable;
};

const onSearchInput = debounce(() => {
  getSignedDeals();
}, 500);

onMounted(() => {
  if (isCheckboxChecked.value) {
    selectedOffices.value = [];
  }
  getSignedDeals();
});
</script>

<template>
  <div class="stepper-subscription choose-office">
    <h3 class="stepper-subscription__title">
      {{ trans('gestion.subscription.step.2.title') }}
    </h3>
    <div class="stepper-subscription__search">
      <input
        v-model="searchTerm"
        type="text"
        :placeholder="trans('gestion.subscription.step.2.search.placeholder')"
        @input="onSearchInput"
      >
    </div>
    <div class="stepper-subscription__choices">
      <div v-for="office in offices" :key="office.id" class="option">
        <input
          :id="office.id"
          v-model="selectedOffices"
          type="checkbox"
          :value="office.id"
          @change="onOfficeCheckboxChange"
        >
        <label :for="office.id">
          {{ getOfficeCheckboxLabel(office) }}
        </label>
      </div>
    </div>
    <div class="stepper-subscription__other">
      <input
        id="newOfficeCheckbox"
        v-model="isCheckboxChecked"
        type="checkbox"
        @change="onNewOfficeCheckboxChange"
      >
      <label for="newOfficeCheckbox">{{ trans('gestion.subscription.step.2.outsider', { company_name: $companyName }) }}</label>
    </div>
    <panel
      type="info"
      :content="trans('gestion.subscription.step.2.panel')"
      icon="ph_lightbulb-fill_blue"
    />
    <button
      class="btn btn--primary stepper-subscription__button"
      :disabled="isNextDisabled"
      @click="onClickNext"
    >
      {{ trans('global.pagination.next') }}
    </button>
  </div>
</template>
